import { useEffect, useRef } from "react";

import { earliestSignedLinkExpiryMs, isSignedLinkStale } from "../pwa/signedLinks";

/** Cada cuánto se revisa, con la pantalla a la vista, si las ligas ya van a vencer. */
export const SIGNED_LINK_CHECK_EVERY_MS = 60 * 1000;
/** Nunca más de una recarga automática en este lapso (p. ej. si el reloj del teléfono está mal). */
export const SIGNED_LINK_MIN_REFRESH_GAP_MS = 10 * 60 * 1000;

/**
 * Pantallas que muestran ligas firmadas (comprobante, CURP, foto) y se quedan
 * abiertas mucho tiempo: Finanzas, la inscripción de la familia…
 *
 * Las ligas vencen en una hora. Si la pantalla no vuelve a pedir sus datos,
 * cada clic abre «Esta liga ya venció» aunque el usuario regrese y lo intente
 * otra vez. Este hook llama `refresh` (sin spinner: la pantalla decide) cuando
 * la primera liga está por vencer y la pantalla está a la vista: al volver a
 * la pestaña, al enfocar la ventana o en la revisión de cada minuto.
 */
export function useSignedLinkRefresh(
  urls: ReadonlyArray<string | null | undefined>,
  refresh: () => void | Promise<void>,
  enabled = true,
): void {
  const expiry = earliestSignedLinkExpiryMs(urls);
  const refreshRef = useRef(refresh);
  const lastRefreshRef = useRef(0);
  const inFlightRef = useRef(false);

  useEffect(() => {
    refreshRef.current = refresh;
  });

  useEffect(() => {
    if (!enabled || expiry === null || typeof window === "undefined") return undefined;

    function maybeRefresh() {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      if (!isSignedLinkStale(expiry)) return;
      const now = Date.now();
      if (inFlightRef.current || now - lastRefreshRef.current < SIGNED_LINK_MIN_REFRESH_GAP_MS) return;
      lastRefreshRef.current = now;
      inFlightRef.current = true;
      let pending: Promise<void>;
      try {
        pending = Promise.resolve(refreshRef.current());
      } catch {
        pending = Promise.resolve();
      }
      pending
        .catch(() => undefined)
        .finally(() => {
          inFlightRef.current = false;
        });
    }

    maybeRefresh();
    const interval = window.setInterval(maybeRefresh, SIGNED_LINK_CHECK_EVERY_MS);
    document.addEventListener("visibilitychange", maybeRefresh);
    window.addEventListener("focus", maybeRefresh);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", maybeRefresh);
      window.removeEventListener("focus", maybeRefresh);
    };
  }, [enabled, expiry]);
}
